// Builds and triggers a download for the one-page summary PDF. Extracted from
// the inline logic in SummaryPdfButton so the gated export wizard and the
// dev-fallback toolbox button share the same pipeline. `options` lets the
// caller suppress sections — the underlying SummaryPdfData type already has
// nullable thumbnail/cost and an empty-array fallback for zones, so we can
// honour them with a small post-process step here.

import { buildSummaryData, type SummaryPdfData } from "src/components/SummaryPdf/buildSummaryData";
import { ENV } from "src/constants/env";
import { RenderRgWorld } from "src/utils/RenderRgWorld";
import type { Api } from "src/types/Api";
import type { MapState } from "src/types/MapState";
import type { World } from "src/types/World";

export interface GenerateAndDownloadInput {
  world: World;
  projectId: string;
  projectName: string;
  api: Api;
  mapState: MapState;
  options?: {
    include3DThumbnail?: boolean;
    includeCarbonSummary?: boolean;
    includeZonesTable?: boolean;
  };
  metadata?: {
    /** Overrides the default `data.designName` used in headings and filename. */
    name?: string;
  };
  /** Pre-captured site plan (e.g. a forced top-down, buildings-hidden shot); skips the internal capture. */
  sitePlanOverride?: string | null;
}

export interface GenerateAndDownloadResult {
  designName: string;
  hasCarbon: boolean;
  zonesCount: number;
  buildingsCount: number;
}

/**
 * Captures the live-map + offscreen images for one design and builds its
 * SummaryPdfData. Split out so a multi-design report can collect several and
 * render them into a single document. Captures from `input.mapState` — so for a
 * combined report, point that map state at each design before calling.
 */
export async function buildSummaryPdfData(
  input: GenerateAndDownloadInput
): Promise<SummaryPdfData> {
  const opts = {
    include3DThumbnail: true,
    includeCarbonSummary: true,
    includeZonesTable: true,
    ...(input.options ?? {}),
  };

  // Live map snapshots: one with the design on it (basemap + overlay + buildings)
  // for the results context, and one "drawing view" with buildings hidden
  // (basemap + overlay + zone outlines) for the design-input plan. The input plan
  // falls back to a synthetic top-down zones render if the live map can't be
  // captured. The 2D top-down and 3D iso come from the offscreen renderer.
  const rgWorld = input.world.rgWorld;
  // Bearing at the on-map (context) capture — used to rotate the on-map compass.
  const mapBearingDeg = input.mapState.getMapBearing();
  const contextMapDataUrl = await input.mapState.captureMapImage();
  const sitePlanDataUrl =
    input.sitePlanOverride !== undefined
      ? input.sitePlanOverride
      : ((await input.mapState.captureMapImage({ hideBuildings: true })) ??
        (await RenderRgWorld(rgWorld, [0, -1, 0], 480, "zones")));
  const topDownDataUrl = await RenderRgWorld(rgWorld, [0, -1, 0], 480, "buildings");
  const thumbnailDataUrl = opts.include3DThumbnail
    ? await RenderRgWorld(rgWorld, [1, -1, 1], 480, "buildings")
    : null;

  const data: SummaryPdfData = await buildSummaryData({
    world: input.world,
    projectId: input.projectId,
    projectName: input.projectName,
    api: input.api,
    mapboxToken: ENV.MAPBOX_KEY,
    stencilMode: input.mapState.stencil.mode,
    sitePlanDataUrl,
    topDownDataUrl,
    thumbnailDataUrl,
    contextMapDataUrl,
    mapBearingDeg,
  });

  if (input.metadata?.name) data.designName = input.metadata.name;
  if (!opts.includeCarbonSummary) data.carbon = null;
  if (!opts.includeZonesTable) data.zones = [];
  return data;
}

export async function generateAndDownloadSummaryPdf(
  input: GenerateAndDownloadInput
): Promise<GenerateAndDownloadResult> {
  const [{ pdf }, { SummaryPdfDocument }] = await Promise.all([
    import("@react-pdf/renderer"),
    import("src/components/SummaryPdf/SummaryPdfDocument"),
  ]);

  const data = await buildSummaryPdfData(input);

  const blob = await pdf(<SummaryPdfDocument data={data} />).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${sanitizeFilename(data.designName)}-summary-${toDateStamp(data.exportedAt)}.pdf`;
  a.click();
  a.remove();
  URL.revokeObjectURL(url);

  return {
    designName: data.designName,
    hasCarbon: data.carbon != null,
    zonesCount: data.zones.length,
    buildingsCount: data.metrics.buildingsCount,
  };
}

function sanitizeFilename(name: string): string {
  const cleaned = name.replace(/[\/\\:*?"<>|]/g, "_").trim();
  return cleaned.length > 0 ? cleaned : "tessa-design";
}

function toDateStamp(d: Date): string {
  return d.toISOString().slice(0, 10);
}
